// Shared formatting/calculation helpers for trip fuel & odometer fields, sourced from the
// original raw trip API record (see TripsList.jsx's `raw` field on each mapped row).

export const isBlankValue = (value) =>
  value === null || value === undefined || value === "";

export const parseNumericValue = (value) => {
  if (isBlankValue(value)) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const formatNumberValue = (value, maximumFractionDigits = 2) => {
  if (isBlankValue(value)) return "-";
  const numericValue = parseNumericValue(value);
  if (numericValue === null) return String(value);
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
  }).format(numericValue);
};

export const formatFuelValue = (value) => {
  if (isBlankValue(value)) return "-";
  const num = parseNumericValue(value);
  if (num === null) return "-";
  return formatNumberValue(Math.max(0, num));
};

export const formatTextValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatVehicleTypeValue = (row) => {
  const rawValue =
    row?.vehicle_type?.title ||
    row?.vehicle_type?.name ||
    row?.vehicle?.vehicle_type?.title ||
    row?.vehicle?.vehicle_type?.name ||
    row?.vehicle_type_name ||
    row?.vehicleType ||
    row?.vehicle?.ownership_type ||
    row?.ownership_type;

  if (isBlankValue(rawValue)) return "-";

  const normalizedValue = String(rawValue).trim().toLowerCase();
  if (normalizedValue === "owned") return "Purchased";
  if (normalizedValue === "hired") return "Hired";

  return formatTextValue(rawValue);
};

export const calculateTatDays = (startAt, actualEndAt) => {
  if (!startAt || !actualEndAt) return "-";
  const startDate = new Date(startAt);
  const endDate = new Date(actualEndAt);
  if (
    isNaN(startDate.getTime()) ||
    isNaN(endDate.getTime()) ||
    endDate < startDate
  ) {
    return "-";
  }

  const dayInMs = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.ceil((endDate - startDate) / dayInMs));
};

export const calculateTotalFuel = (row) => {
  const openingFuel = parseNumericValue(row?.opening_fuel) || 0;
  const allocatedFuel = parseNumericValue(row?.allocated_fuel) || 0;

  if (isBlankValue(row?.opening_fuel) && isBlankValue(row?.allocated_fuel))
    return "-";
  return formatFuelValue(openingFuel + allocatedFuel);
};

export const calculateBalanceFuel = (row) => {
  if (!isBlankValue(row?.closing_fuel))
    return formatFuelValue(row.closing_fuel);

  const totalFuel =
    (parseNumericValue(row?.opening_fuel) || 0) +
    (parseNumericValue(row?.allocated_fuel) || 0);
  const utilizedFuel = parseNumericValue(row?.utilized_fuel);
  if (utilizedFuel === null) return "-";

  return formatFuelValue(totalFuel - utilizedFuel);
};

export const calculateKmPerLiter = (row) => {
  const distance = parseNumericValue(row?.total_odm);
  const utilizedFuel = parseNumericValue(row?.utilized_fuel);
  if (distance === null || !utilizedFuel || utilizedFuel <= 0) return "-";
  return formatNumberValue(distance / utilizedFuel);
};

export const getLoadingPoint = (row) =>
  row?.start_trip?.warehouse?.warehouse_name ||
  row?.start_trip?.warehouse?.name ||
  row?.start_trip?.warehouse_name ||
  row?.dispatch_point ||
  "-";

export const getDecantingPoint = (row) =>
  row?.end_trip?.warehouse?.warehouse_name ||
  row?.end_trip?.warehouse?.name ||
  row?.end_trip?.warehouse_name ||
  row?.decanting_point ||
  "-";

export const formatSettlementValue = (value) => {
  if (value === null || value === undefined) return "-";
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "-";
  return formatNumberValue(num);
};

export const getTripRevenue = (row) =>
  formatSettlementValue(row?.raw?.settlement?.invoice_gross_amount);

export const getFuelExpense = (row) => {
  const s = row?.raw?.settlement;
  if (s?.total_fuel_expense != null) return formatSettlementValue(s.total_fuel_expense);
  if (row?.raw?.calc_fuel_expense != null) return formatSettlementValue(row.raw.calc_fuel_expense);
  return "-";
};

export const getInsuranceAmount = (row) =>
  formatSettlementValue(row?.raw?.settlement?.total_insurance_expense);

export const getShortageAmount = (row) => {
  const s = row?.raw?.settlement;
  if (s?.total_shortage_expense != null) return formatSettlementValue(s.total_shortage_expense);
  const qty = row?.raw?.shortage_qty;
  if (qty != null && qty !== "") return formatSettlementValue(qty);
  return "-";
};

export const getTripExpense = (row) => {
  const s = row?.raw?.settlement;
  if (s?.total_operational_expenses != null) return formatSettlementValue(s.total_operational_expenses);
  if (row?.raw?.calc_trip_expense != null) return formatSettlementValue(row.raw.calc_trip_expense);
  return "-";
};

export const getPettyCashAllocated = (row) =>
  formatSettlementValue(row?.raw?.allocated_petty_cash);

export const getCreatedUser = (row) =>
  row?.raw?.creator?.username || "-";

export const getCreatedDate = (row) => {
  const d = row?.raw?.created_at;
  if (!d) return "-";
  const date = new Date(d);
  if (!Number.isFinite(date.getTime())) return "-";
  return date.toLocaleString("en-PK", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

export const getPettyCashUsed = (row) =>
  formatSettlementValue(row?.raw?.calc_petty_cash_used);
